import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { useMediaQuery, Box, Container, Toolbar } from '@mui/material';

// third-party
import Intercom from '@intercom/messenger-js-sdk';

// project-imports
import Drawer from './Drawer';
import Header from './Header';
// import LayoutCard from './LayoutCard';
import LayoutCard from './BannerCard';
import Footer from './Footer';
import HorizontalBar from './Drawer/HorizontalBar';
import Breadcrumbs from 'components/@extended/Breadcrumbs';
import FloatingOffer from 'components/offers/FloatingOffer';
import OfferDrawer from 'components/offers/OfferDrawer';
import FloatingDTDevelopment from 'components/dt-developments/FloatingDTDevelopment';
import DTDevelopmentDrawer from 'components/dt-developments/DTDevelopmentDrawer';
import InternationalOfferDialog from 'components/InternationalOfferDialog';
import { DRAWER_WIDTH, USE_INTERCOM } from 'config';
// import navigation from 'menu-items';
import useAuth from 'hooks/useAuth';
import useConfig from 'hooks/useConfig';
import { useOffer } from 'contexts/OfferContext';
import { useDTDevelopment } from 'contexts/DTDevelopmentContext';
import { dispatch } from 'store';
import { openDrawer } from 'store/reducers/menu';

// types
import { MenuOrientation } from 'types/config';

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const theme = useTheme();
  const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const downXL = useMediaQuery(theme.breakpoints.down('xl'));
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { container, miniDrawer, menuOrientation } = useConfig();

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  // [ Manage session & Redirect to account if trial-end]
  const { verifySessionDetails } = useAuth();
  const [trialEnd, setTrialEnd] = useState(false);

  useEffect(() => {
    if (pathname) {
      verifySessionDetails()
        .then((res) => {} /*console.log(res)*/)
        .catch((err) => console.error(err));

      if (trialEnd && !pathname.includes('account')) {
        navigate('/account/subscription');
        // console.log('It should redirect -.-');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, trialEnd]);

  // set media wise responsive drawer
  useEffect(() => {
    if (!miniDrawer) {
      dispatch(openDrawer(!downXL));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downXL]);

  const { user, userDetails } = useAuth();

  const [trial, setTrial] = useState(false);

  useEffect(() => {
    if (user?.currentPlan?.includes('trial')) {
      setTrial(true);
      if (user.currentPlan === 'trial-end') {
        setTrialEnd(true);
      }
    }
  }, [user?.currentPlan]);

  // [ OFFERS ]
  const { isOfferEnabled, toggleOfferDrawer } = useOffer();

  // [ DT DEVELOPMENTS ]
  const { isDTDevelopmentEnabled, toggleDTDevelopmentDrawer } = useDTDevelopment();

  // [ INTERCOM ]
  const [usrDetails, setUsrDetails] = useState<any>();

  useEffect(() => {
    if (!USE_INTERCOM) return;

    if (user?.email) {
      // if (user.email === 'franpercedel@gmail.com' || user.email === 'barbara@homiia.com') {
      handleUserDetails(user.email);
      // }
    }
  }, [user?.email]);

  const handleUserDetails = async (email: string) => {
    const userDet = await userDetails(email);
    setUsrDetails(userDet);
  };

  // const getDateDDMMYYYY = (date: any) => {
  //   let day = date.getDate();
  //   let month = date.getMonth() + 1;
  //   let year = date.getFullYear();

  //   day = day < 10 ? `0${day}` : day;
  //   month = month < 10 ? `0${month}` : month;

  //   return `${day}-${month}-${year}`;
  // };

  useEffect(() => {
    if (!USE_INTERCOM) return;

    // const excludingPathList = ['tools', 'development'];
    const excludingPathList: any[] = [];
    const isExcludedPath = excludingPathList.some((excludedPath) => pathname.includes(excludedPath));

    if (
      !isExcludedPath &&
      user &&
      user.id &&
      user.name &&
      user.email &&
      usrDetails &&
      usrDetails.UserCreateDate &&
      user.currentPlan?.includes('pro')
    ) {
      Intercom({
        app_id: 'i8afoajr',
        user_id: user.id,
        name: user.name,
        email: user.email,
        created_at: usrDetails.UserCreateDate,
        custom_launcher_selector: '#btn_custom_intercom'
      });
    }
  }, [user, usrDetails, pathname]);

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <Header />
      {!isHorizontal ? <Drawer /> : <HorizontalBar />}

      <Box component="main" sx={{ width: `calc(100% - ${DRAWER_WIDTH}px)`, flexGrow: 1, p: { xs: 2, md: 3 } }}>
        <Toolbar sx={{ mt: isHorizontal ? 8 : 'inherit', mb: isHorizontal ? 2 : 'inherit' }} />
        <Container
          maxWidth={container ? 'xl' : false}
          sx={{
            xs: 0,
            ...(container && { px: { xs: 0, md: 2 } }),
            position: 'relative',
            minHeight: 'calc(100vh - 110px)',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {!pathname.includes('account') && <Breadcrumbs title titleBottom card={false} divider={false} />}
          {trial && !pathname.includes('account') && (
            <Box sx={{ mt: -3, mb: matchDownMd ? 0 : 3 }}>
              <LayoutCard />
            </Box>
          )}
          <Outlet />
          {trial && !pathname.includes('account') && <LayoutCard />}
          <Footer />
        </Container>
      </Box>

      {isOfferEnabled && <FloatingOffer onClick={toggleOfferDrawer} />}
      <OfferDrawer />
      {isDTDevelopmentEnabled && <FloatingDTDevelopment onClick={toggleDTDevelopmentDrawer} />}
      <DTDevelopmentDrawer />
      <InternationalOfferDialog />
    </Box>
  );
};

export default MainLayout;
